import { useEffect, useState }             from "react";
import { useParams }                       from "react-router";
import { NavLink }                         from "react-router-dom";
import { ToggleButton, ToggleButtonGroup } from "react-bootstrap";
import { AxiosResponse }                   from "axios";
/* Project References */
import { Event, Event2LocationPost }           from "../../../models/Event";
import { DistanceBandSet } from "../../../models/Admin/DistanceBands";
import msalRestClient      from "../../../services/clients/MsalRestClient";
import DeleteDialog        from "../../../components/DeleteDialog";
import Event2Contact from "./Event2Contact";
import Event2Location from "./Event2Location";
import LinkModal from "../../../components/LinkModal";

type FormState = {
    activeTab    : number,
    errors       : string[],
    success?     : string,
    showLinkModal? : boolean
}

const EventDetail = () => {

    // #region Init States

    let initEvent: Event = {
        id          : -1,
        number      : '00XX123',
        title       : 'Eventtitle',
        location    : 'Trier',
        startDate   : new Date(),
        endDate     : new Date(),
        offlineDate : new Date(),
        accommodationEligible : false,
        distanceBandVersion   : 1
    }

    // #endregion

    // #region States & Hooks

    const { id } = useParams();

    const [event, setEvent]                 = useState<Event>(initEvent)
    const [distanceBands, setDistanceBands] = useState<DistanceBandSet[]>([]);

    const [formState, setFormState] = useState<FormState>({ activeTab: 0, errors: [], showLinkModal: false });

    useEffect(() => { fetchFromApi(); }, []);

    // #endregion

    // #region Functions

    /**
     * ...
     */
    const fetchFromApi = () => {

        Promise.all([
            msalRestClient.get<Event>("/api/events/" + id),
            msalRestClient.get<DistanceBandSet[]>("/api/distancebands")
        ])
            .then(response => onApiSuccess(response))
            .catch(error   => console.error(error));
    }

    /**
     * ...
     * @param param
     */
    const onApiSuccess = ([eventResponse, distanceBandsResponse]: [AxiosResponse<Event, any>, AxiosResponse<DistanceBandSet[], any>]) => {

        if (eventResponse.status === 200) {
            setEvent(eventResponse.data);
        }
        if (distanceBandsResponse.status === 200) {
            setDistanceBands(distanceBandsResponse.data);
        }
    }

    /**
     * 
     * @param eventResponse
     */
    const onApiSuccess02 = (eventResponse: AxiosResponse<Event, any>) => {

        if (eventResponse.status === 200) {
            setEvent(eventResponse.data);
        }
    }

    /**
     * ...
     */
    const onSubmit = () => {

        // #region Validation

        let errors: string[] = [];

        if (event.number === '') { errors.push("Field 'Number' is mandatory.") }
        if (event.title === '')  { errors.push("Field 'Title' is mandatory.") }

        if (!event.startDate) { errors.push("Field 'StartDate' is mandatory.") }
        if (!event.endDate)   { errors.push("Field 'EndDate' is mandatory.") }

        if (event.distanceBandVersion === -1) { errors.push("Field 'DistanceBandVersion' is mandatory.") }

        // #endregion

        if (errors.length === 0) {

            msalRestClient.put<Event>("/api/events", event)
                .then(response => { setFormState(x => ({ ...x, errors: [], success: "The event has been successfully updated." })) })
                .catch(err     => { setFormState(x => ({ ...x, errors: ["Failed to update current event."] })) });

        }
        else { setFormState(x => ({ ...x, errors: errors, success: undefined })) }
    }

    /**
     * ...
     * @param index
     */
    const handleContactDelete = (index?: number) => {

        let contact = event.contacts?.at(index ?? -1);
        if (contact) {

            msalRestClient.delete<Event>("/api/events/" + event.id + "/contacts/" + contact.contactId)
                .then(response => onApiSuccess02(response))
                .catch(error   => console.error(error));
        }
    }

    /**
     * ...
     * @param contacts
     */
    const handleContactsAdd = (contacts: number[]) => {

        msalRestClient.post<Event>("/api/events/" + event.id + "/contacts", contacts)
            .then(response => onApiSuccess02(response))
            .catch(error   => console.error(error));

    }

    /**
     * ...
     * @param index
     */
    const handleLocationDelete = (index?: number) => {

        let location = event.locations?.at(index ?? -1);
        if (location) {

            msalRestClient.delete<Event>("/api/events/" + event.id + "/locations/" + location.locationId)
                .then(response => onApiSuccess02(response))
                .catch(error   => console.error(error));
        }
    }

    /**
     * ...
     * @param location
     */
    const handleLocationAdd = (location: Event2LocationPost) => {

        let postLocation: Event2LocationPost = { ...location, eventId: event.id };

        msalRestClient.post<Event>("/api/events/locations", postLocation)
            .then(response => onApiSuccess02(response))
            .catch(error   => console.error(error));
    }

    // #region Render Functions

    /**
     * Method renders alert with missing mandatory field informations.
     * TO-DO: Extract in on components that takes an array with errors as parameter.
     * @returns JSX.Element representing a boostrap alert alert-danger.
     */
    const renderErrorPane = () => {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="alert alert-danger">
                        <p className="fw-bold mb-0">Whoops! There were some problems with your input.</p>
                        <ul className="mb-0">
                            {
                                formState.errors.map((value, index) => {
                                    return (
                                        <li key={"err_" + index}>{value}</li>
                                    )
                                })
                            }
                        </ul>
                    </div>
                </div>
            </div>
        )
    }

    /**
     * Methods renders alert with success message to user. 
     * @returns JSX.Element representing a boostrap alert alert-success
     */
    const renderSuccessPane = () => {
        return (
            <div className="row">
                <div className="col-12">
                    <div className="alert alert-success alert-dismissible fade show" role="alert">
                        <i className="bi bi-check-circle me-2"></i>
                        {formState.success}
                        <button type="button" className="btn-close" onClick={e => setFormState(x => ({ ...x, success: undefined }))} />
                    </div>
                </div>
            </div>
        )
    }

    // #endregion

    // #endregion

    // JSX Element for rendering in browser
    return (
        <main className="container mb-4">

            <div className="row mb-2">
                <div className="col-12">
                    <NavLink to="/admin/events" className="btn btn-dark">
                        <i className="bi bi-arrow-left"></i>
                    </NavLink>
                </div>
            </div>

            {(formState.success) && renderSuccessPane()}
            {(formState.errors.length > 0) && renderErrorPane()}

            <LinkModal link={window.location.origin + "/claims/create/" + event.number} show={formState.showLinkModal ?? false} onClose={() => setFormState(x => ({ ...x, showLinkModal: false }))} />

            { /* Tabs | Tab-Navigation */}

            <div className="row">

                <div className="col-12 col-sm-1 pe-0">
                    { /* Nav-Links */ }
                    <ul className="nav flex-column nav-pills text-center">

                        <li className="nav-item">
                            <a className={"nav-link text-light" + (formState.activeTab === 0 ? " active" : " bg-dark")} href="#home" onClick={e => setFormState(x => ({ ...x, activeTab: 0 }))}>
                                <i className="bi bi-pen"></i>
                            </a>
                        </li>

                        <li className="nav-item mt-1">
                            <a className={"nav-link text-light" + (formState.activeTab === 1 ? " active" : " bg-dark")} href="#contacts" onClick={e => setFormState(x => ({ ...x, activeTab: 1 }))}>
                                <i className="bi bi-people"></i>
                            </a>
                        </li>

                        <li className="nav-item mt-1">
                            <a className={"nav-link text-light" + (formState.activeTab === 2 ? " active" : " bg-dark")} href="#places" onClick={e => setFormState(x => ({ ...x, activeTab: 2 }))}>
                                <i className="bi bi-geo"></i>
                            </a>
                        </li>

                    </ul>
                </div>

                <div className="col-12 col-sm-11 ps-0">

                    <div className="card" style={{ borderTopLeftRadius: 0 }}>
                        <div className="card-body">

                            { /* Tab-Content Panes */}

                            <div className="tab-content">

                                <div className={"tab-pane fade" + (formState.activeTab === 0 ? " show active" : "")}>

                                    <div className="row">

                                        <div className="col-12">
                                            <h4>Event Core Data</h4>
                                            <hr />
                                        </div>

                                        <div className="form-group col-12 col-md-4">
                                            <label className="fw-bold" htmlFor="number">Number</label>
                                            <input type="text" className="form-control" id="number" value={event.number} onChange={e => setEvent(x => ({ ...x, number: e.target.value }))} />
                                        </div>
                                        <div className="form-group col-12 col-md-8">
                                            <label className="fw-bold" htmlFor="title">Title</label>
                                            <input type="text" className="form-control" id="title" value={event.title} onChange={e => setEvent(x => ({ ...x, title: e.target.value }))} />
                                        </div>

                                        <div className="col-12">
                                            <hr/>
                                        </div>

                                    </div>

                                    <div className="row">

                                        <div className="form-group col-12 col-md-4">
                                            <label className="fw-bold" htmlFor="starDate">Seminar Start Date</label>
                                            <input type="date" className="form-control" id="startDate" value={new Date(event.startDate).toLocaleDateString("en-ca", { timeZone: "Europe/Berlin" })} onChange={e => setEvent(x => ({ ...x, startDate: new Date(e.target.value) }))} />
                                        </div>
                                        <div className="form-group col-12 col-md-4">
                                            <label className="fw-bold" htmlFor="endDate">Seminar End Date</label>
                                            <input type="date" className="form-control" id="endDate" value={new Date(event.endDate).toLocaleDateString("en-ca", { timeZone: "Europe/Berlin" })} onChange={e => setEvent(x => ({ ...x, endDate: new Date(e.target.value) }))} />
                                        </div>
                                        <div className="form-group col-12 col-md-4">
                                            <label className="fw-bold" htmlFor="offlineDate">Offline Date</label>
                                            <input type="date" className="form-control" id="offlineDate" value={new Date(event.offlineDate).toLocaleDateString("en-ca", { timeZone: "Europe/Berlin" })} onChange={e => setEvent(x => ({ ...x, offlineDate: new Date(e.target.value) }))} />
                                        </div>

                                    </div>

                                    <div className="row">

                                        <div className="form-group col-12 col-md-4">
                                            <label className="fw-bold" htmlFor="version">Distance Bands Version</label>
                                            <select className="form-select" id="version" value={event.distanceBandVersion} onChange={e => setEvent(x => ({ ...x, distanceBandVersion: Number(e.target.value) }))}>
                                                <option value={-1}>Please select version</option>
                                                {
                                                    distanceBands.map((value, index) =>
                                                    {
                                                        return (
                                                            <option key={index}>{value.version}</option>
                                                        )
                                                    })
                                                }
                                            </select>
                                        </div>

                                        <div className="form-group col-12 col-md-4">
                                            <label className="fw-bold" htmlFor="accommodation">Accommodation Eligible</label>
                                            <ToggleButtonGroup className="w-100" type="radio" name="accommodation" value={event.accommodationEligible ? 1 : 0} onChange={(value, e) => setEvent(x => ({ ...x, accommodationEligible: (Number(value) === 1) }))}>
                                                <ToggleButton id="btn-radio-1" value={0} className="btn btn-light">
                                                    No
                                                </ToggleButton>
                                                <ToggleButton id="btn-radio-2" value={1} className="btn btn-light">
                                                    Yes
                                                </ToggleButton>
                                            </ToggleButtonGroup>
                                        </div>

                                        <div className="form-group col-12 col-md-4">
                                            <label className="fw-bold" htmlFor="maxNights">Max. Nights</label>
                                            <input type="number" name="maxNights" min={-1} className="form-control" value={event.maxNights} onChange={e => setEvent(x => ({ ...x, maxNights: Number(e.target.value) }))} />
                                        </div>

                                    </div>

                                </div>

                                <div className={"tab-pane fade" + (formState.activeTab === 1 ? " show active" : "")}>

                                    <div className="row">

                                        <div className="col-12">
                                            <h4>Employees</h4>
                                        </div>

                                        <div className="col-12">
                                            <hr />

                                            <Event2Contact onSuccess={handleContactsAdd} />

                                            <hr />
                                        </div>

                                        <div className="co-12">
                                            <table className="table table-striped">
                                                <thead>
                                                    <tr key={"thkey01"}>
                                                        <th>Id</th>
                                                        <th>Name</th>
                                                        <th>E-Mail</th>
                                                        <th>Job-Title</th>
                                                        <th>
                                                            { /* Empty heading for control */ }
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        event.contacts?.map((value, index) => {
                                                            return (
                                                                <tr>
                                                                    <td>{value.contactId}</td>
                                                                    <td>{value.displayName}</td>
                                                                    <td>{value.email}</td>
                                                                    <td>{value.jobTitle}</td>
                                                                    <td>
                                                                        <DeleteDialog index={index} title="Please confirm the deletion." description="Do you really want to delete the record?" btnClass="btn btn-danger" btnIcon="bi bi-trash" onConfirm={handleContactDelete} key={"btnDelete" + index} />
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })
                                                    }
                                                </tbody>
                                            </table>
                                        </div>

                                    </div>

                                </div>

                                <div className={"tab-pane fade" + (formState.activeTab === 2 ? " show active" : "")}>

                                    <div className="row">

                                        <div className="col-12">
                                            <h4>Locations</h4>
                                        </div>

                                        <div className="col-12">
                                            <hr />

                                            <Event2Location onSuccess={handleLocationAdd} />

                                            <hr />
                                        </div>

                                        <div className="co-12">
                                            <table className="table table-striped">
                                                <thead>
                                                    <tr>
                                                        <th>Id</th>
                                                        <th>Name</th>
                                                        <th>Country</th>
                                                        <th>
                                                            { /* Empty heading for control */}
                                                        </th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {
                                                        event.locations?.map((value, index) =>
                                                        {
                                                            return (
                                                                <tr>
                                                                    <td>{value.locationId}</td>
                                                                    <td>{value.name}</td>
                                                                    <td>{value.country}</td>
                                                                    <td>
                                                                        <DeleteDialog index={index} title="Please confirm the deletion." description="Do you really want to delete the record?" btnClass="btn btn-danger" btnIcon="bi bi-trash" onConfirm={handleLocationDelete} key={"btnDelete" + index} />
                                                                    </td>
                                                                </tr>
                                                            )
                                                        })
                                                    }
                                                </tbody>
                                            </table>
                                        </div>

                                    </div>

                                </div>

                            </div>

                        </div>
                        <div className="card-footer">
                            <div className="row">
                                <div className="col-12 text-end">
                                    <button className="btn btn-success me-1" onClick={onSubmit}>
                                        <i className="bi bi-save" /> Save Changes
                                    </button>
                                    <button className="btn btn-primary" onClick={e => setFormState(x => ({ ...x, showLinkModal: true }))}>
                                        <i className="bi bi-clipboard me-1" /> Show Link
                                    </button>
                                </div>
                            </div>
                        </div>

                    </div>

                </div>
            </div>

        </main>
    )
}

export default EventDetail;